import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import WebhooksTab from './WebhooksTab';
import ValidationRulesPanel from './ValidationRulesPanel';
import IconPickerInline from './IconPickerInline';

const ICON_CAPABLE_TYPES = ['input', 'email', 'phone', 'number', 'url', 'date'];

// ── Reusable color picker row ────────────────────────────────────────────────
const ColorPicker = ({ label, value, defaultValue, onChange, onApplyAll, description = '' }) => {
  const [applied, setApplied] = React.useState(false);
  const displayValue = value || defaultValue || '#000000';

  const handleApplyAll = () => {
    onApplyAll?.();
    setApplied(true);
    setTimeout(() => setApplied(false), 1800);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {onApplyAll && (
          <button
            type="button"
            onClick={handleApplyAll}
            title={`Apply this ${label.toLowerCase()} to all fields`}
            className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-all ${
              applied
                ? 'text-success bg-success/10'
                : 'text-muted-foreground hover:text-primary hover:bg-primary/8'
            }`}
          >
            <Icon name={applied ? 'CheckCheck' : 'CopyCheck'} size={11} />
            {applied ? 'Applied!' : 'Apply to all'}
          </button>
        )}
      </div>
      <div className="flex gap-2 items-center">
        <div className="relative flex-shrink-0">
          <input
            type="color"
            value={displayValue?.startsWith('#') ? displayValue : defaultValue || '#000000'}
            onChange={(e) => onChange(e?.target?.value)}
            className="w-10 h-10 rounded-md border border-border cursor-pointer p-0.5 bg-background"
            title={`Pick ${label}`}
          />
        </div>
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e?.target?.value)}
          placeholder={defaultValue || '#000000'}
          className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </div>
  );
};

const SettingsPanel = ({ selectedElement, onUpdateElement, onApplyStyleToAll, onClose, formId, formTitle, form, onUpdateForm, allElements, onElementDelete, onSave }) => {
  const [activeTab, setActiveTab] = useState('general');

  if (!selectedElement) {
    return (
      <div className="w-80 lg:w-80 md:w-64 sm:w-full bg-card border-l border-border flex items-center justify-center p-8 flex-shrink-0 overflow-hidden">
        <div className="text-center">
          <Icon name="Settings" size={48} color="var(--color-muted-foreground)" />
          <p className="text-sm text-muted-foreground mt-4">
            Select an element to edit its properties
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'General', icon: 'Settings' },
    { id: 'style', name: 'Style', icon: 'Palette' },
    { id: 'validation', name: 'Validation', icon: 'CheckCircle2' },
    { id: 'logic', name: 'Logic', icon: 'GitBranch' },
    { id: 'webhooks', name: 'Webhooks', icon: 'Webhook' },
    { id: 'payment', name: 'Payment', icon: 'CreditCard' }
  ];

  const fontFamilyOptions = [
    { value: 'inter', label: 'Inter' },
    { value: 'roboto', label: 'Roboto' },
    { value: 'opensans', label: 'Open Sans' },
    { value: 'lato', label: 'Lato' },
    { value: 'montserrat', label: 'Montserrat' }
  ];

  const fontSizeOptions = [
    { value: '12', label: '12px' },
    { value: '14', label: '14px' },
    { value: '16', label: '16px' },
    { value: '18', label: '18px' },
    { value: '20', label: '20px' }
  ];

  const fontWeightOptions = [
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' }
  ];

  // Helper: update a fieldStyle property and propagate via onUpdateElement
  const updateFieldStyle = (patch) => {
    const current = selectedElement?.fieldStyle || {};
    onUpdateElement({ ...selectedElement, fieldStyle: { ...current, ...patch } });
  };

  // Helper: apply a style patch to all elements
  const applyToAll = (patch, options = {}) => onApplyStyleToAll?.(patch, options);

  const renderGeneralSettings = () => {
    // Button-specific settings
    if (selectedElement?.type === 'button') {
      return (
        <div className="space-y-4">
          {/* Live Button Preview */}
          <div className="p-6 bg-muted/30 rounded-lg border border-border">
            <label className="text-xs font-medium text-muted-foreground mb-3 block">Live Preview</label>
            <div className="flex items-center justify-center">
              <button
                style={{
                  backgroundColor: selectedElement?.buttonColor || '#0e7572',
                  color: selectedElement?.buttonTextColor || '#FFFFFF',
                  width: `${selectedElement?.buttonWidth || 200}px`,
                  height: `${selectedElement?.buttonHeight || 44}px`,
                  borderRadius: `${selectedElement?.borderRadius || 6}px`,
                  fontSize: `${selectedElement?.fontSize || 16}px`,
                  fontWeight: selectedElement?.fontWeight || '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                className="shadow-sm hover:opacity-90"
              >
                {selectedElement?.buttonText || 'Submit'}
              </button>
            </div>
          </div>

          <Input
            label="Button Text"
            type="text"
            value={selectedElement?.buttonText || 'Submit'}
            onChange={(e) => onUpdateElement({ ...selectedElement, buttonText: e?.target?.value })}
            placeholder="Enter button text"
          />

          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="button_name"
            description="Used for form submission data"
          />

          {/* Button Size Sliders */}
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Button Width: {selectedElement?.buttonWidth || 200}px
              </label>
              <input
                type="range"
                min="80"
                max="400"
                value={selectedElement?.buttonWidth || 200}
                onChange={(e) => onUpdateElement({ ...selectedElement, buttonWidth: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                style={{ accentColor: 'var(--color-primary)' }}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>80px</span>
                <span>400px</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Button Height: {selectedElement?.buttonHeight || 44}px
              </label>
              <input
                type="range"
                min="32"
                max="80"
                value={selectedElement?.buttonHeight || 44}
                onChange={(e) => onUpdateElement({ ...selectedElement, buttonHeight: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                style={{ accentColor: 'var(--color-primary)' }}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>32px</span>
                <span>80px</span>
              </div>
            </div>
          </div>

          <Checkbox
            label="Disabled"
            checked={selectedElement?.disabled || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, disabled: e?.target?.checked })}
          />
        </div>
      );
    }

    // Signature-specific settings
    if (selectedElement?.type === 'signature') {
      return (
        <div className="space-y-4">
          <Input
            label="Label"
            type="text"
            value={selectedElement?.label || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
            placeholder="Enter label text"
          />

          <Input
            label="Description"
            type="text"
            value={selectedElement?.description || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
            placeholder="Helper text"
          />

          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="signature_field"
            description="Used for form submission data"
          />

          <div className="space-y-3 pt-2">
            <h4 className="text-sm font-medium text-foreground">Signature Configuration</h4>
            
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Width"
                type="number"
                value={selectedElement?.width || 300}
                onChange={(e) => onUpdateElement({ ...selectedElement, width: parseInt(e?.target?.value) })}
                placeholder="300"
              />
              <Input
                label="Height"
                type="number"
                value={selectedElement?.height || 100}
                onChange={(e) => onUpdateElement({ ...selectedElement, height: parseInt(e?.target?.value) })}
                placeholder="100"
              />
            </div>
          </div>

          <Checkbox
            label="Required Field"
            checked={selectedElement?.required || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          />
        </div>
      );
    }

    // Media/Image (logo) settings
    if (selectedElement?.type === 'media') {
      return (
        <div className="space-y-4">
          {/* Live image preview */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <label className="text-xs font-medium text-muted-foreground mb-3 block">Current Logo / Image</label>
            {selectedElement?.src ? (
              <div className="flex justify-center">
                <img
                  src={selectedElement?.src}
                  alt={selectedElement?.alt || 'Form header image'}
                  className="max-h-20 object-contain rounded"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-16 bg-muted rounded border-2 border-dashed border-border">
                <span className="text-xs text-muted-foreground">No image set</span>
              </div>
            )}
          </div>
          {/* Upload Logo button */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Upload Logo</label>
            <p className="text-xs text-muted-foreground mb-2">Upload an image file from your device</p>
            <label
              className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-md border border-border bg-background text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50 transition-colors"
              style={{ minHeight: '38px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Choose File
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e?.target?.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    onUpdateElement({ ...selectedElement, src: ev?.target?.result });
                  };
                  reader?.readAsDataURL(file);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <Input
            label="Image URL"
            type="url"
            value={selectedElement?.src || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, src: e?.target?.value })}
            placeholder="https://example.com/your-logo.png"
            description="Paste the URL of your logo or image"
          />
          <Input
            label="Alt Text"
            type="text"
            value={selectedElement?.alt || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, alt: e?.target?.value })}
            placeholder="Company logo"
            description="Describes the image for accessibility"
          />
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-3">
              Don't want a logo on your form? You can remove this section entirely.
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={() => {
                if (onElementDelete) {
                  onElementDelete(selectedElement?.id);
                }
              }}
            >
              Remove Logo Section
            </Button>
          </div>
        </div>
      );
    }

    // Content (heading / paragraph) settings
    if (selectedElement?.type === 'content') {
      const isHeading = selectedElement?.id?.startsWith('heading') || selectedElement?.contentType === 'heading';
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {isHeading ? 'Heading Text' : 'Paragraph Text'}
            </label>
            <textarea
              value={selectedElement?.content || ''}
              onChange={(e) => onUpdateElement({ ...selectedElement, content: e?.target?.value })}
              placeholder={isHeading ? 'Enter heading text' : 'Enter paragraph text'}
              rows={isHeading ? 2 : 4}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="content_field"
            description="Used for form submission data"
          />
        </div>
      );
    }

    // Toggle-specific settings
    if (selectedElement?.type === 'toggle') {
      return (
        <div className="space-y-4">
          <Input
            label="Label"
            type="text"
            value={selectedElement?.label || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
            placeholder="Enter label text"
          />
          <Input
            label="Description"
            type="text"
            value={selectedElement?.description || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
            placeholder="Helper text shown under the label"
          />
          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="toggle_field"
            description="Used for form submission data"
          />
          <div className="space-y-3 pt-2 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">State Labels (optional)</h4>
            <Input
              label="On Label"
              type="text"
              value={selectedElement?.onLabel || ''}
              onChange={(e) => onUpdateElement({ ...selectedElement, onLabel: e?.target?.value })}
              placeholder="On"
              description="Text shown when the toggle is on"
            />
            <Input
              label="Off Label"
              type="text"
              value={selectedElement?.offLabel || ''}
              onChange={(e) => onUpdateElement({ ...selectedElement, offLabel: e?.target?.value })}
              placeholder="Off"
              description="Text shown when the toggle is off"
            />
          </div>
          <Checkbox
            label="Required Field (must be switched on)"
            checked={selectedElement?.required || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          />
        </div>
      );
    }

    // Slider-specific settings
    if (selectedElement?.type === 'slider') {
      return (
        <div className="space-y-4">
          <Input
            label="Label"
            type="text"
            value={selectedElement?.label || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
            placeholder="Enter label text"
          />
          <Input
            label="Description"
            type="text"
            value={selectedElement?.description || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
            placeholder="Helper text shown under the label"
          />
          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="slider_field"
            description="Used for form submission data"
          />
          <div className="space-y-3 pt-2 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">Range Configuration</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Min"
                type="number"
                value={selectedElement?.sliderMin ?? 0}
                onChange={(e) => onUpdateElement({ ...selectedElement, sliderMin: Number(e?.target?.value) })}
                placeholder="0"
              />
              <Input
                label="Max"
                type="number"
                value={selectedElement?.sliderMax ?? 100}
                onChange={(e) => onUpdateElement({ ...selectedElement, sliderMax: Number(e?.target?.value) })}
                placeholder="100"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Step"
                type="number"
                value={selectedElement?.sliderStep ?? 1}
                onChange={(e) => onUpdateElement({ ...selectedElement, sliderStep: Number(e?.target?.value) })}
                placeholder="1"
              />
              <Input
                label="Default Value"
                type="number"
                value={selectedElement?.sliderDefault ?? 50}
                onChange={(e) => onUpdateElement({ ...selectedElement, sliderDefault: Number(e?.target?.value) })}
                placeholder="50"
              />
            </div>
          </div>
          <Checkbox
            label="Required Field"
            checked={selectedElement?.required || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          />
        </div>
      );
    }

    // Star Rating settings
    if (selectedElement?.type === 'rating') {
      return (
        <div className="space-y-4">
          <Input
            label="Label"
            type="text"
            value={selectedElement?.label || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
            placeholder="Rate your experience"
          />
          <Input
            label="Description"
            type="text"
            value={selectedElement?.description || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
            placeholder="Helper text shown under the label"
          />
          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="rating_field"
            description="Used for form submission data"
          />
          <div className="space-y-3 pt-2 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">Rating Configuration</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Max Stars"
                type="number"
                value={selectedElement?.ratingMax ?? 5}
                onChange={(e) => {
                  const newMax = Math.max(1, Math.min(20, Number(e?.target?.value) || 5));
                  const currentDefault = selectedElement?.ratingDefault ?? 1;
                  onUpdateElement({
                    ...selectedElement,
                    ratingMax: newMax,
                    ratingDefault: Math.min(currentDefault, newMax),
                  });
                }}
                placeholder="5"
              />
              <Input
                label="Default"
                type="number"
                value={selectedElement?.ratingDefault ?? 1}
                onChange={(e) => {
                  const max = selectedElement?.ratingMax ?? 5;
                  const val = Math.max(1, Math.min(max, Number(e?.target?.value) || 1));
                  onUpdateElement({ ...selectedElement, ratingDefault: val });
                }}
                placeholder="1"
              />
            </div>
          </div>
          <Checkbox
            label="Required Field"
            checked={selectedElement?.required || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          />
        </div>
      );
    }

    // Numeric Scale settings
    if (selectedElement?.type === 'scale') {
      return (
        <div className="space-y-4">
          <Input
            label="Label"
            type="text"
            value={selectedElement?.label || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
            placeholder="How likely are you to recommend?"
          />
          <Input
            label="Description"
            type="text"
            value={selectedElement?.description || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
            placeholder="Helper text shown under the label"
          />
          <Input
            label="Field Name"
            type="text"
            value={selectedElement?.name || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
            placeholder="scale_field"
            description="Used for form submission data"
          />
          <div className="space-y-3 pt-2 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">Scale Configuration</h4>
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Min"
                type="number"
                value={selectedElement?.scaleMin ?? 1}
                onChange={(e) => {
                  const newMin = Number(e?.target?.value) || 1;
                  const currentMax = selectedElement?.scaleMax ?? 10;
                  const safeMax = Math.max(newMin + 1, currentMax);
                  const currentDefault = selectedElement?.scaleDefault;
                  onUpdateElement({
                    ...selectedElement,
                    scaleMin: newMin,
                    scaleMax: safeMax,
                    ...(currentDefault != null ? { scaleDefault: Math.max(newMin, Math.min(safeMax, currentDefault)) } : {}),
                  });
                }}
                placeholder="1"
              />
              <Input
                label="Max"
                type="number"
                value={selectedElement?.scaleMax ?? 10}
                onChange={(e) => {
                  const currentMin = selectedElement?.scaleMin ?? 1;
                  const newMax = Math.max(currentMin + 1, Number(e?.target?.value) || 10);
                  const currentDefault = selectedElement?.scaleDefault;
                  onUpdateElement({
                    ...selectedElement,
                    scaleMax: newMax,
                    ...(currentDefault != null ? { scaleDefault: Math.min(currentDefault, newMax) } : {}),
                  });
                }}
                placeholder="10"
              />
              <Input
                label="Default"
                type="number"
                value={selectedElement?.scaleDefault ?? ''}
                onChange={(e) => {
                  const min = selectedElement?.scaleMin ?? 1;
                  const max = selectedElement?.scaleMax ?? 10;
                  const raw = Number(e?.target?.value);
                  if (!e?.target?.value) {
                    onUpdateElement({ ...selectedElement, scaleDefault: undefined });
                  } else {
                    onUpdateElement({ ...selectedElement, scaleDefault: Math.max(min, Math.min(max, raw)) });
                  }
                }}
                placeholder="auto"
              />
            </div>
            <p className="text-xs text-muted-foreground">Default is optional; omitting it uses the midpoint.</p>
          </div>
          <Checkbox
            label="Required Field"
            checked={selectedElement?.required || false}
            onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          />
        </div>
      );
    }

    // Default settings for other elements
    return (
      <div className="space-y-4">
        <Input
          label="Label"
          type="text"
          value={selectedElement?.label || ''}
          onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
          placeholder="Enter label text"
        />
        <Input
          label="Placeholder"
          type="text"
          value={selectedElement?.placeholder || ''}
          onChange={(e) => onUpdateElement({ ...selectedElement, placeholder: e?.target?.value })}
          placeholder="Enter placeholder text"
        />
        <Input
          label="Description"
          type="text"
          value={selectedElement?.description || ''}
          onChange={(e) => onUpdateElement({ ...selectedElement, description: e?.target?.value })}
          placeholder="Helper text"
        />
        <Input
          label="Field Name"
          type="text"
          value={selectedElement?.name || ''}
          onChange={(e) => onUpdateElement({ ...selectedElement, name: e?.target?.value })}
          placeholder="field_name"
          description="Used for form submission data"
        />

        {/* Dropdown Options Manager */}
        {selectedElement?.type === 'select' && (
          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Dropdown Options</h4>
              <button
                type="button"
                onClick={() => {
                  const currentOptions = selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'];
                  onUpdateElement({ ...selectedElement, options: [...currentOptions, `Option ${currentOptions?.length + 1}`] });
                }}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <Icon name="Plus" size={14} />
                Add Option
              </button>
            </div>

            <div className="space-y-2">
              {(selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])?.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const updated = [...(selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])];
                      updated[idx] = e?.target?.value;
                      onUpdateElement({ ...selectedElement, options: updated });
                    }}
                    className="flex-1 text-sm px-2 py-1.5 rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`Option ${idx + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updated = (selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])?.filter((_, i) => i !== idx);
                      onUpdateElement({ ...selectedElement, options: updated?.length > 0 ? updated : ['Option 1'] });
                    }}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                    title="Remove option"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              ))}
            </div>

            {(selectedElement?.options || [])?.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No options yet. Click "Add Option" to begin.</p>
            )}
          </div>
        )}

        {/* Radio / Checkbox Options Manager */}
        {(selectedElement?.type === 'radio' || selectedElement?.type === 'checkbox') && (
          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">
                {selectedElement?.type === 'radio' ? 'Radio Options' : 'Checkbox Options'}
              </h4>
              <button
                type="button"
                onClick={() => {
                  const currentOptions = selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'];
                  onUpdateElement({ ...selectedElement, options: [...currentOptions, `Option ${currentOptions?.length + 1}`] });
                }}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <Icon name="Plus" size={14} />
                Add Option
              </button>
            </div>

            <div className="space-y-2">
              {(selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])?.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className={`w-4 h-4 border-2 border-input flex-shrink-0 ${selectedElement?.type === 'radio' ? 'rounded-full' : 'rounded'}`} />
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const updated = [...(selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])];
                      updated[idx] = e?.target?.value;
                      onUpdateElement({ ...selectedElement, options: updated });
                    }}
                    className="flex-1 text-sm px-2 py-1.5 rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={`Option ${idx + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updated = (selectedElement?.options || ['Option 1', 'Option 2', 'Option 3'])?.filter((_, i) => i !== idx);
                      onUpdateElement({ ...selectedElement, options: updated?.length > 0 ? updated : ['Option 1'] });
                    }}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                    title="Remove option"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              ))}
            </div>

            {(selectedElement?.options || [])?.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No options yet. Click "Add Option" to begin.</p>
            )}
          </div>
        )}

        <Checkbox
          label="Required Field"
          checked={selectedElement?.required || false}
          onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
        />
      </div>
    );
  };

  const renderStyleSettings = () => {
    // Button-specific style settings
    if (selectedElement?.type === 'button') {
      return (
        <div className="space-y-4">
          {/* Live Button Preview */}
          <div className="p-6 bg-muted/30 rounded-lg border border-border">
            <label className="text-xs font-medium text-muted-foreground mb-3 block">Live Preview</label>
            <div className="flex items-center justify-center">
              <button
                style={{
                  backgroundColor: selectedElement?.buttonColor || '#0e7572',
                  color: selectedElement?.buttonTextColor || '#FFFFFF',
                  width: `${selectedElement?.buttonWidth || 200}px`,
                  height: `${selectedElement?.buttonHeight || 44}px`,
                  borderRadius: `${selectedElement?.borderRadius || 6}px`,
                  fontSize: `${selectedElement?.fontSize || 16}px`,
                  fontWeight: selectedElement?.fontWeight || '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                className="shadow-sm hover:opacity-90"
              >
                {selectedElement?.buttonText || 'Submit'}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Colors</h4>

            <ColorPicker
              label="Button Color"
              value={selectedElement?.buttonColor}
              defaultValue="#0e7572"
              onChange={(val) => onUpdateElement({ ...selectedElement, buttonColor: val })}
              onApplyAll={() => applyToAll({ buttonColor: selectedElement?.buttonColor || '#0e7572' }, { mode: 'direct', typeFilter: 'button' })}
              description="Default: App Green (#0e7572)"
            />

            <ColorPicker
              label="Text Color"
              value={selectedElement?.buttonTextColor}
              defaultValue="#FFFFFF"
              onChange={(val) => onUpdateElement({ ...selectedElement, buttonTextColor: val })}
              onApplyAll={() => applyToAll({ buttonTextColor: selectedElement?.buttonTextColor || '#FFFFFF' }, { mode: 'direct', typeFilter: 'button' })}
            />
          </div>

          <div className="pt-4 border-t border-border space-y-3">
            <h4 className="text-sm font-medium text-foreground">Typography</h4>
            
            <Select
              label="Font Size"
              options={fontSizeOptions}
              value={String(selectedElement?.fontSize || '16')}
              onChange={(value) => onUpdateElement({ ...selectedElement, fontSize: parseInt(value) })}
            />

            <Select
              label="Font Weight"
              options={fontWeightOptions}
              value={String(selectedElement?.fontWeight || '500')}
              onChange={(value) => onUpdateElement({ ...selectedElement, fontWeight: value })}
            />
          </div>

          <div className="pt-4 border-t border-border space-y-3">
            <h4 className="text-sm font-medium text-foreground">Border</h4>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Border Radius: {selectedElement?.borderRadius || 6}px
              </label>
              <input
                type="range"
                min="0"
                max="32"
                value={selectedElement?.borderRadius || 6}
                onChange={(e) => onUpdateElement({ ...selectedElement, borderRadius: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0px</span>
                <span>32px</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Content elements (heading, paragraph) style settings
    if (selectedElement?.type === 'content') {
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Typography</h4>

            <Select
              label="Font Size"
              options={fontSizeOptions}
              value={String(selectedElement?.fontSize || '16')}
              onChange={(value) => onUpdateElement({ ...selectedElement, fontSize: parseInt(value) })}
            />

            <Select
              label="Font Weight"
              options={fontWeightOptions}
              value={String(selectedElement?.fontWeight || '400')}
              onChange={(value) => onUpdateElement({ ...selectedElement, fontWeight: value })}
            />
          </div>

          <div className="pt-4 border-t border-border space-y-3">
            <h4 className="text-sm font-medium text-foreground">Colors</h4>

            <ColorPicker
              label="Text Color"
              value={selectedElement?.labelColor}
              defaultValue="#1F2937"
              onChange={(val) => onUpdateElement({ ...selectedElement, labelColor: val })}
              onApplyAll={() => applyToAll({ labelColor: selectedElement?.labelColor || '#1F2937' }, { mode: 'direct', typeFilter: 'content' })}
            />
          </div>
        </div>
      );
    }

    // Media/Image style settings — only expose image size here (canvas toolbar also has it)
    if (selectedElement?.type === 'media') {
      const imgSize = selectedElement?.fieldStyle?.imageSize ?? selectedElement?.imageSize ?? 100;
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Image Size: {imgSize}%
            </label>
            <input
              type="range"
              min="10"
              max="200"
              value={imgSize}
              onChange={(e) => {
                const val = parseInt(e?.target?.value);
                onUpdateElement({
                  ...selectedElement,
                  imageSize: val,
                  fieldStyle: { ...(selectedElement?.fieldStyle || {}), imageSize: val },
                });
              }}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              style={{ accentColor: 'var(--color-primary)' }}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>10%</span>
              <span>200%</span>
            </div>
          </div>
        </div>
      );
    }

    // Default style settings for input, select, checkbox, radio, file, signature
    // These write to fieldStyle.* which FormCanvas reads via buildElementStyles()
    const fs = selectedElement?.fieldStyle || {};

    return (
      <div className="space-y-4">
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Typography</h4>
          
          <Select
            label="Font Size"
            options={fontSizeOptions}
            value={String(fs?.fontSize || '14')}
            onChange={(value) => updateFieldStyle({ fontSize: parseInt(value) })}
          />

          <Select
            label="Input Font Weight"
            options={fontWeightOptions}
            value={String(fs?.inputFontWeight || '400')}
            onChange={(value) => updateFieldStyle({ inputFontWeight: value })}
          />

          <Select
            label="Label Font Weight"
            options={fontWeightOptions}
            value={String(fs?.labelFontWeight || '500')}
            onChange={(value) => updateFieldStyle({ labelFontWeight: value })}
          />

          <Select
            label="Label Alignment"
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
            value={fs?.labelTextAlign || 'left'}
            onChange={(value) => updateFieldStyle({ labelTextAlign: value })}
          />
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">Colors</h4>

          <ColorPicker
            label="Label Color"
            value={fs?.labelColor}
            defaultValue="#1F2937"
            onChange={(val) => updateFieldStyle({ labelColor: val })}
            onApplyAll={() => applyToAll({ labelColor: fs?.labelColor || '#1F2937' })}
          />

          <ColorPicker
            label="Input Background"
            value={fs?.inputBg}
            defaultValue="#FFFFFF"
            onChange={(val) => updateFieldStyle({ inputBg: val })}
            onApplyAll={() => applyToAll({ inputBg: fs?.inputBg || '#FFFFFF' })}
          />

          <ColorPicker
            label="Text Color"
            value={fs?.textColor}
            defaultValue="#1F2937"
            onChange={(val) => updateFieldStyle({ textColor: val })}
            onApplyAll={() => applyToAll({ textColor: fs?.textColor || '#1F2937' })}
          />

          <ColorPicker
            label="Border Color"
            value={fs?.borderColor}
            defaultValue="#E5E7EB"
            onChange={(val) => updateFieldStyle({ borderColor: val })}
            onApplyAll={() => applyToAll({ borderColor: fs?.borderColor || '#E5E7EB' })}
          />
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">Size & Spacing</h4>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Input Height: {fs?.inputHeight || 40}px
            </label>
            <input
              type="range"
              min="28"
              max="80"
              value={fs?.inputHeight || 40}
              onChange={(e) => updateFieldStyle({ inputHeight: parseInt(e?.target?.value) })}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              style={{ accentColor: 'var(--color-primary)' }}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>28px</span>
              <span>80px</span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Border Radius: {fs?.borderRadius ?? 6}px
            </label>
            <input
              type="range"
              min="0"
              max="32"
              value={fs?.borderRadius ?? 6}
              onChange={(e) => updateFieldStyle({ borderRadius: parseInt(e?.target?.value) })}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              style={{ accentColor: 'var(--color-primary)' }}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0px</span>
              <span>32px</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Padding X: {fs?.paddingX ?? 12}px</label>
              <input
                type="range"
                min="4"
                max="32"
                value={fs?.paddingX ?? 12}
                onChange={(e) => updateFieldStyle({ paddingX: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Padding Y: {fs?.paddingY ?? 8}px</label>
              <input
                type="range"
                min="4"
                max="24"
                value={fs?.paddingY ?? 8}
                onChange={(e) => updateFieldStyle({ paddingY: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Margin Top: {fs?.marginTop ?? 0}px</label>
              <input
                type="range"
                min="0"
                max="32"
                value={fs?.marginTop ?? 0}
                onChange={(e) => updateFieldStyle({ marginTop: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Margin Bottom: {fs?.marginBottom ?? 0}px</label>
              <input
                type="range"
                min="0"
                max="32"
                value={fs?.marginBottom ?? 0}
                onChange={(e) => updateFieldStyle({ marginBottom: parseInt(e?.target?.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
            </div>
          </div>
        </div>

        {/* Input Icon — only for icon-capable field types */}
        {ICON_CAPABLE_TYPES?.includes(selectedElement?.type) && (
          <div className="pt-4 border-t border-border space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="Smile" size={14} color="var(--color-primary)" />
              <h4 className="text-sm font-medium text-foreground">Input Icon</h4>
              {fs?.iconName && (
                <button
                  type="button"
                  onClick={() => updateFieldStyle({ iconName: undefined, iconPosition: undefined })}
                  className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
                >
                  <Icon name="X" size={11} />
                  Clear
                </button>
              )}
            </div>
            <IconPickerInline
              value={fs?.iconName}
              onChange={(v) => updateFieldStyle({ iconName: v })}
            />
            {fs?.iconName && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Icon Position</label>
                <div className="grid grid-cols-2 gap-2">
                  {['left', 'right']?.map(pos => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => updateFieldStyle({ iconPosition: pos })}
                      className={`py-2 px-3 text-xs font-medium rounded-md border transition-colors ${
                        (fs?.iconPosition || 'left') === pos
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50 text-muted-foreground'
                      }`}
                    >
                      {pos === 'left' ? 'Left' : 'Right'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {fs?.iconName && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">Icon Color</label>
                  <div className="flex items-center gap-1">
                    {fs?.iconColor && (
                      <button
                        type="button"
                        onClick={() => updateFieldStyle({ iconColor: undefined })}
                        title="Reset icon colour to default (inherits input text colour)"
                        className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                      >
                        <Icon name="X" size={11} />
                        Reset
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => applyToAll({ iconColor: fs?.iconColor || fs?.textColor || '#9ca3af' })}
                      title="Apply this icon colour to all fields"
                      className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/8 transition-all"
                    >
                      <Icon name="CopyCheck" size={11} />
                      Apply to all
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-shrink-0">
                    <input
                      type="color"
                      value={(fs?.iconColor || fs?.textColor || '#9ca3af')?.startsWith?.('#') ? (fs?.iconColor || fs?.textColor || '#9ca3af') : '#9ca3af'}
                      onChange={(e) => updateFieldStyle({ iconColor: e?.target?.value })}
                      className="w-10 h-10 rounded-md border border-border cursor-pointer p-0.5 bg-background"
                      title="Pick Icon Color"
                    />
                  </div>
                  <input
                    type="text"
                    value={fs?.iconColor || fs?.textColor || '#9ca3af'}
                    onChange={(e) => updateFieldStyle({ iconColor: e?.target?.value })}
                    placeholder={fs?.textColor || '#9ca3af'}
                    className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}
            {fs?.iconName && (
              <div className="pt-2">
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Preview</label>
                <div
                  className="flex items-center rounded-md border overflow-hidden"
                  style={{
                    borderColor: fs?.borderColor || '#E5E7EB',
                    backgroundColor: fs?.inputBg || '#FFFFFF',
                    height: `${fs?.inputHeight || 40}px`,
                    paddingLeft: (fs?.iconPosition || 'left') === 'left' ? `${(fs?.paddingX ?? 12) / 2}px` : `${fs?.paddingX ?? 12}px`,
                    paddingRight: (fs?.iconPosition || 'left') === 'right' ? `${(fs?.paddingX ?? 12) / 2}px` : `${fs?.paddingX ?? 12}px`,
                    borderRadius: `${fs?.borderRadius ?? 6}px`,
                    gap: '6px',
                    flexDirection: (fs?.iconPosition || 'left') === 'right' ? 'row-reverse' : 'row',
                  }}
                >
                  <span className="flex-shrink-0 flex items-center" style={{ color: fs?.iconColor || fs?.textColor || '#9ca3af' }}>
                    <Icon name={fs.iconName} size={15} />
                  </span>
                  <span
                    className="flex-1 text-xs truncate"
                    style={{
                      color: fs?.textColor ? `${fs.textColor}66` : '#9ca3af',
                      fontSize: `${fs?.fontSize || 14}px`,
                    }}
                  >
                    Sample text…
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reset styles */}
        <div className="pt-2 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
            className="w-full text-muted-foreground"
            onClick={() => onUpdateElement({ ...selectedElement, fieldStyle: {} })}
          >
            Reset Field Styles
          </Button>
        </div>
      </div>
    );
  };

  const renderValidationSettings = () => (
    <ValidationRulesPanel
      element={selectedElement}
      allElements={allElements || []}
      onUpdate={onUpdateElement}
    />
  );

  const renderLogicSettings = () => (
    <div className="space-y-4">
      <div className="p-4 bg-muted/50 rounded-md border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Conditional Logic</h4>
            <p className="text-xs text-muted-foreground">
              Show or hide this field based on other field values
            </p>
          </div>
        </div>
      </div>

      <Checkbox
        label="Enable Conditional Logic"
        checked={selectedElement?.hasLogic || false}
        onChange={(e) => onUpdateElement({ ...selectedElement, hasLogic: e?.target?.checked })}
      />

      {selectedElement?.hasLogic && (
        <div className="space-y-3 p-4 bg-muted/30 rounded-md">
          <Select
            label="Show this field when"
            options={[
              { value: 'equals', label: 'Field equals' },
              { value: 'notEquals', label: 'Field does not equal' },
              { value: 'contains', label: 'Field contains' },
              { value: 'greaterThan', label: 'Field is greater than' }
            ]}
            value={selectedElement?.logicCondition || 'equals'}
            onChange={(value) => onUpdateElement({ ...selectedElement, logicCondition: value })}
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Target Field</label>
            {(allElements || [])?.filter(el => el?.id !== selectedElement?.id && el?.name)?.length > 0 ? (
              <Select
                options={(allElements || [])
                  ?.filter(el => el?.id !== selectedElement?.id && el?.name)
                  ?.map(el => ({ value: el?.name || el?.id, label: el?.label || el?.name || el?.id }))}
                value={selectedElement?.logicTargetField || ''}
                onChange={(value) => onUpdateElement({ ...selectedElement, logicTargetField: value })}
              />
            ) : (
              <Input
                type="text"
                value={selectedElement?.logicTargetField || ''}
                onChange={(e) => onUpdateElement({ ...selectedElement, logicTargetField: e?.target?.value })}
                placeholder="field_name"
                description="Enter the field name to watch"
              />
            )}
          </div>

          <Input
            label="Value"
            type="text"
            value={selectedElement?.logicTargetValue || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, logicTargetValue: e?.target?.value })}
            placeholder="expected value"
            description="Show this field when the target field matches this value"
          />

          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">
              {selectedElement?.logicTargetField && selectedElement?.logicTargetValue
                ? `This field will show when "${selectedElement?.logicTargetField}" ${selectedElement?.logicCondition || 'equals'} "${selectedElement?.logicTargetValue}"`
                : 'Set a target field and value above to configure when this field appears.'}
            </p>
            {selectedElement?.logicTargetField && selectedElement?.logicTargetValue && (
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => onUpdateElement({
                  ...selectedElement,
                  hasLogic: false,
                  logicCondition: '',
                  logicTargetField: '',
                  logicTargetValue: ''
                })}
              >
                Clear Logic Rule
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-80 lg:w-80 md:w-64 sm:w-full bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="text-base font-heading font-semibold text-foreground">
          Element Settings
        </h3>
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth"
          aria-label="Close settings"
        >
          <Icon name="X" size={20} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="border-b border-border">
        <div className="flex overflow-x-auto scrollbar-custom">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                border-b-2 transition-smooth
                ${activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-custom p-4">
        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'style' && renderStyleSettings()}
        {activeTab === 'validation' && renderValidationSettings()}
        {activeTab === 'logic' && renderLogicSettings()}
        {activeTab === 'webhooks' && formId && (
          <WebhooksTab formId={formId} formTitle={formTitle} />
        )}
      </div>
      <div className="p-4 border-t border-border">
        <Button
          variant="default"
          fullWidth
          iconName="Save"
          iconPosition="left"
          onClick={() => onSave && onSave()}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default SettingsPanel;